import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Sparkles, ArrowRight, X } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = '';

export default function TrialBanner() {
  const [trialData, setTrialData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if banner was dismissed in this session
    const wasDismissed = sessionStorage.getItem('trial_banner_dismissed');
    if (wasDismissed) {
      setDismissed(true);
      setLoading(false);
      return;
    }
    
    fetchTrialStatus();
  }, []);

  const fetchTrialStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/trial-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTrialData(data);
      }
    } catch (error) {
      console.error('Failed to fetch trial status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('trial_banner_dismissed', 'true');
  };

  // Don't show if loading, dismissed, not on trial, or trial ended
  if (loading || dismissed || !trialData?.is_trial || trialData?.trial_ended) {
    return null;
  }

  const daysRemaining = trialData.days_remaining || 0;
  const isUrgent = daysRemaining <= 2;
  const isWarning = daysRemaining <= 4 && !isUrgent;

  return (
    <div 
      className={cn(
        "relative overflow-hidden rounded-xl mb-6 transition-all",
        isUrgent 
          ? "bg-gradient-to-r from-red-500 to-orange-500" 
          : isWarning 
            ? "bg-gradient-to-r from-amber-500 to-orange-500"
            : "bg-gradient-to-r from-orange-500 to-pink-500"
      )}
      data-testid="trial-banner"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white" />
        <div className="absolute -left-5 -bottom-5 w-24 h-24 rounded-full bg-white" />
      </div>
      
      <div className="relative px-5 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
            isUrgent ? "bg-red-600/30" : "bg-white/20"
          )}>
            {isUrgent ? (
              <Clock className="w-6 h-6 text-white animate-pulse" />
            ) : (
              <Sparkles className="w-6 h-6 text-white" />
            )}
          </div>
          
          <div className="text-white">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg">
                {isUrgent ? '⚡ Trial Ending Soon!' : '🎉 You\'re on a Free Trial'}
              </h3>
            </div>
            <p className="text-sm text-white/90 mt-0.5">
              {daysRemaining === 0 ? (
                'Your trial ends today! Upgrade now to keep all your data and features.'
              ) : daysRemaining === 1 ? (
                'Only 1 day left! Upgrade to continue using all premium features.'
              ) : (
                <>
                  <span className="font-bold">{daysRemaining} days</span> remaining in your trial. 
                  Upgrade anytime to unlock unlimited access.
                </>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <Link
            to="/admin/billing"
            className={cn(
              "inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all",
              isUrgent 
                ? "bg-white text-red-600 hover:bg-red-50 shadow-lg" 
                : "bg-white text-orange-600 hover:bg-orange-50 shadow-lg"
            )}
            data-testid="trial-upgrade-btn"
          >
            Upgrade Now
            <ArrowRight className="w-4 h-4" />
          </Link>
          
          <button
            onClick={handleDismiss}
            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Progress bar showing trial usage */}
      <div className="h-1 bg-white/20">
        <div 
          className="h-full bg-white/50 transition-all duration-500"
          style={{ width: `${Math.max(0, ((5 - daysRemaining) / 5) * 100)}%` }}
        />
      </div>
    </div>
  );
}
